/**
 * Werving — /werving/sollicitatie/:id: sollicitantkaart (Homerun "Application review" + cv/tijdlijn).
 *
 * Links de stapel van deze vacature (264 px, eigen kolom), midden brief + cv (pdf inline via de proxy,
 * niets opgeslagen) + portfolio + AI-samenvatting (feitelijk, ingeklapt), rechts de scorecard met tijdlijn en
 * notities. Sneltoetsen: j/k vorige/volgende, 1–4 score op het actieve criterium, g/x goed-/afkeuren,
 * Enter indienen en door naar de volgende die jij nog moet beoordelen.
 *
 * Blind scoren zit in de API: andermans scores komen pas mee nadat jij hebt ingediend.
 * "Concept klaarzetten" maakt alleen een concept in ikwilwerken@ — er wordt nooit iets verstuurd.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Menu, MenuItem, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Portfolio from './Portfolio';
import { Antwoordadres, KoppelVelden } from './KaartActies';
import { Avatar, FaseChip } from './WervingUi';
import toast from 'react-hot-toast';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { bevestig } from '../../utils/bevestig';
import { haalBijlage, haalCorrespondentieBijlage, wervingApi } from './wervingApi';
import {
  ACCENT, ACCENT_ZACHT, EIND_FASEN, INKT, KOLOM_BG, LIJN, OORDEEL_KLEUR, OORDEEL_LABEL, SCORE_KLEUR, STIL, beheerPad, bewaarLabel,
  briefDelen, buren, conceptAdviesTekst, dagenGeleden, datumTijd, delenLengte, faseKleur, gemiddelden, gesprekLabel, knipDelen,
  naarLokaalInvoer,
  vanLokaalInvoer,
  scorecardCompleet, sneltoets, telOordelen, volgendeTeBeoordelen,
} from './wervingLogica';

const kop = { fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: STIL };

function Bijlage({ sid, b }) {
  const [url, setUrl] = useState(null);
  const [fout, setFout] = useState(null);
  useEffect(() => {
    if (!b.inline) return undefined;
    let weg = false; let u = null;
    setUrl(null); setFout(null);
    haalBijlage(sid, b.index).then((blob) => { if (weg) return; u = URL.createObjectURL(blob); setUrl(u); })
      .catch((e) => !weg && setFout(e.status === 404 ? 'Niet meer in de mailbox.' : e.message));
    return () => { weg = true; if (u) URL.revokeObjectURL(u); };
  }, [sid, b.index, b.inline]);

  const download = async () => {
    if (!(await bevestig({ titel: `${b.naam} downloaden?`, tekst: 'Het bestand komt uit ikwilwerken@ en wordt op jouw computer opgeslagen. Verwijder het als je klaar bent (AVG).', knop: 'Downloaden' }))) return;
    try {
      const blob = await haalBijlage(sid, b.index);
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = u; a.download = b.naam; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 5000);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Box sx={{ border: `1px solid ${LIJN}`, borderRadius: 3, overflow: 'hidden', mb: 2, bgcolor: '#fff' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.75, py: 1, gap: 1, borderBottom: b.inline ? `1px solid ${LIJN}` : 'none' }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: INKT, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.naam}</Typography>
        {b.inline ? (
          <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />} disabled={!url} onClick={() => window.open(url, '_blank', 'noopener')}
            sx={{ textTransform: 'none', color: ACCENT }}>Origineel openen</Button>
        ) : <Button size="small" onClick={download} sx={{ textTransform: 'none', color: ACCENT }}>Downloaden</Button>}
      </Box>
      {b.inline && (fout ? <Typography sx={{ p: 2, color: STIL, fontSize: 13 }}>{fout}</Typography>
        : !url ? <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} /></Box>
          : <Box component="iframe" title={b.naam} src={url} sx={{ width: '100%', height: 760, border: 0, display: 'block', bgcolor: KOLOM_BG }} />)}
    </Box>
  );
}

const kopSectie = { ...kop, display: 'flex', alignItems: 'center', gap: 1, mb: 1,
  '&::before': { content: '""', width: 3, height: 12, borderRadius: 2, bgcolor: ACCENT } };

/** Tekst met links als React-elementen (nooit HTML): lange links ingekort, Mail Drop/WeTransfer als bijlageknop. */
function TekstMetLinks({ delen }) {
  return delen.map((d, i) => {
    if (d.soort === 'tekst') return <React.Fragment key={i}>{d.tekst}</React.Fragment>;
    if (d.soort === 'bijlage') {
      return (
        <Box key={i} component="a" href={d.href} target="_blank" rel="noopener noreferrer"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, my: 0.25, borderRadius: 2,
            border: `1px solid ${LIJN}`, bgcolor: KOLOM_BG, color: ACCENT, fontSize: 13, textDecoration: 'none',
            '&:hover': { borderColor: ACCENT } }}>
          <AttachFileIcon sx={{ fontSize: 15 }} />{d.label}
        </Box>
      );
    }
    return (
      <Box key={i} component="a" href={d.href} target="_blank" rel="noopener noreferrer" title={d.href}
        sx={{ color: ACCENT, textDecoration: 'underline', textDecorationColor: '#c5ccd6', wordBreak: 'break-all' }}>{d.label}</Box>
    );
  });
}

function PlatformKaart({ p }) {
  const regel = [p.kandidaat, p.kop, p.locatie].filter(Boolean).join(', ');
  return (
    <Box sx={{ border: `1px solid ${LIJN}`, borderRadius: 2.5, p: 1.75, bgcolor: KOLOM_BG, mb: 1.5 }}>
      <Typography sx={{ fontSize: 14, color: INKT, fontWeight: 600 }}>
        Sollicitatie via {p.platform_label}{p.vacature ? ` op ${p.vacature}` : ''}
      </Typography>
      {regel && <Typography sx={{ fontSize: 13, color: STIL, mt: 0.25 }}>{regel}</Typography>}
      <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
        {p.links?.profiel && <Button size="small" variant="outlined" href={p.links.profiel} target="_blank" rel="noopener noreferrer"
          startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />} sx={{ textTransform: 'none', color: ACCENT, borderColor: LIJN }}>Profiel</Button>}
        {p.links?.cv && <Button size="small" variant="outlined" href={p.links.cv} target="_blank" rel="noopener noreferrer"
          startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />} sx={{ textTransform: 'none', color: ACCENT, borderColor: LIJN }}>
          Cv op {p.platform_label}</Button>}
        {p.links?.bericht && p.alleen_op_platform && <Button size="small" variant="outlined" href={p.links.bericht} target="_blank"
          rel="noopener noreferrer" startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />} sx={{ textTransform: 'none', color: ACCENT, borderColor: LIJN }}>
          Bericht openen op {p.platform_label}</Button>}
      </Box>
      {p.alleen_op_platform && !p.links?.bericht && !p.links?.cv && (
        <Typography sx={{ fontSize: 12.5, color: STIL, mt: 1 }}>De tekst van de kandidaat staat alleen op {p.platform_label}.</Typography>
      )}
    </Box>
  );
}

function Brief({ sid, onPlatform, onAdres }) {
  const [b, setB] = useState(null);
  const [heel, setHeel] = useState(false);
  useEffect(() => {
    let weg = false;
    setB(null); setHeel(false);
    wervingApi.brief(sid).then((x) => { if (weg) return; setB(x); onPlatform?.(x.platform || null); onAdres?.(x.antwoordadres || null); })
      .catch((e) => { if (!weg) { setB({ beschikbaar: false, reden: e.message }); onAdres?.(null); } });
    return () => { weg = true; };
  }, [sid, onPlatform, onAdres]);
  const delen = useMemo(() => briefDelen(b?.tekst || ''), [b]);
  if (!b) return <CircularProgress size={18} />;
  if (!b.beschikbaar) return <Typography sx={{ color: STIL, fontSize: 13.5 }}>Brief niet beschikbaar: {b.reden}</Typography>;
  const lang = delenLengte(delen) > 900;
  return (
    <Box>
      {b.platform && <PlatformKaart p={b.platform} />}
      {b.onderwerp && !b.platform && <Typography sx={{ fontSize: 13, color: STIL, mb: 0.75 }}>{b.onderwerp}</Typography>}
      {(!b.platform || !b.platform.alleen_op_platform) && (
        <Typography component="div" sx={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6, color: INKT, overflowWrap: 'anywhere' }}>
          {delen.length ? <TekstMetLinks delen={lang && !heel ? knipDelen(delen, 900) : delen} /> : '(lege mail)'}
        </Typography>
      )}
      {lang && <Button size="small" onClick={() => setHeel((x) => !x)} sx={{ textTransform: 'none', color: ACCENT, px: 0 }}>{heel ? 'Minder' : 'Hele brief'}</Button>}
    </Box>
  );
}

function CorrespondentieBijlage({ sid, berichtId, a }) {
  const open = async () => {
    if (a.type !== 'application/pdf' && !(await bevestig({ titel: `${a.naam} downloaden?`,
      tekst: 'Het bestand komt uit ikwilwerken@ en wordt op jouw computer opgeslagen. Verwijder het als je klaar bent (AVG).', knop: 'Downloaden' }))) return;
    try {
      const blob = await haalCorrespondentieBijlage(sid, berichtId, a.id);
      const u = URL.createObjectURL(blob);
      if (a.type === 'application/pdf') window.open(u, '_blank', 'noopener');
      else { const el = document.createElement('a'); el.href = u; el.download = a.naam; el.click(); }
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Button size="small" onClick={open} disabled={!a.te_openen} startIcon={<AttachFileIcon sx={{ fontSize: 15 }} />}
      title={a.te_openen ? '' : 'Dit bestandstype wordt niet getoond (alleen pdf, docx, jpg, png tot 10 MB)'}
      sx={{ textTransform: 'none', color: ACCENT, fontSize: 12.5, justifyContent: 'flex-start', px: 0.75 }}>{a.naam}</Button>
  );
}

function Bericht({ sid, m }) {
  const [open, setOpen] = useState(false);
  const delen = useMemo(() => briefDelen(m.tekst), [m.tekst]);
  const verstuurd = m.richting === 'verstuurd';
  const lang = delenLengte(delen) > 700;
  return (
    <Box sx={{ display: 'flex', justifyContent: verstuurd ? 'flex-end' : 'flex-start', mb: 1.5 }}>
      <Box sx={{ width: '100%', maxWidth: 680, borderRadius: 2.5, border: `1px ${m.concept ? 'dashed' : 'solid'} ${verstuurd ? '#d6deea' : LIJN}`,
        bgcolor: verstuurd ? ACCENT_ZACHT : '#fff', px: 1.75, py: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: INKT }}>{verstuurd ? `${m.afzender}` : 'Sollicitant'}</Typography>
          <Typography sx={{ fontSize: 12, color: STIL }}>{verstuurd ? 'verstuurd' : 'binnen'}{m.platform ? ` · via ${m.platform}` : ''} · {datumTijd(m.op)}</Typography>
          {m.concept && <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: '#7a4a00', bgcolor: '#fcf2df', px: 0.75, borderRadius: 1 }}>Concept (nog niet verstuurd)</Box>}
        </Box>
        {m.onderwerp && <Typography sx={{ fontSize: 12.5, color: STIL, mt: 0.25 }}>{m.onderwerp}</Typography>}
        <Typography component="div" sx={{ whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.55, color: INKT, mt: 0.75, overflowWrap: 'anywhere' }}>
          {delen.length ? <TekstMetLinks delen={lang && !open ? knipDelen(delen, 700) : delen} /> : <Box component="span" sx={{ color: STIL }}>(geen tekst)</Box>}
        </Typography>
        {lang && <Button size="small" onClick={() => setOpen((x) => !x)} sx={{ textTransform: 'none', color: ACCENT, px: 0, minWidth: 0 }}>{open ? 'Minder' : 'Hele mail'}</Button>}
        {(m.citaat_weggelaten || m.afgekapt) && (
          <Typography sx={{ fontSize: 11.5, color: STIL, mt: 0.5 }}>
            {[m.citaat_weggelaten && 'eerdere mail (citaat) weggelaten', m.afgekapt && 'afgekapt — lees de rest in Outlook'].filter(Boolean).join(' · ')}
          </Typography>
        )}
        {(m.bijlagen || []).length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mt: 0.75 }}>
            {m.bijlagen.map((a) => <CorrespondentieBijlage key={a.id} sid={sid} berichtId={m.id} a={a} />)}
          </Box>
        )}
        {m.bijlagen_fout && <Typography sx={{ fontSize: 11.5, color: STIL, mt: 0.5 }}>Bijlagen konden niet worden opgehaald.</Typography>}
      </Box>
    </Box>
  );
}

/** Mailwisseling met de sollicitant: pas op verzoek opgehaald (read-only, niets opgeslagen; inzage komt in de audit). */
function Correspondentie({ sid }) {
  const [d, setD] = useState(null);
  const [bezig, setBezig] = useState(false);
  const actueel = useRef(sid);
  actueel.current = sid;
  useEffect(() => { setD(null); }, [sid]);
  const laad = async () => {
    const voor = sid;
    setBezig(true);
    try {
      const x = await wervingApi.correspondentie(voor);
      if (actueel.current === voor) setD(x);
    } catch (e) {
      if (actueel.current === voor) setD({ beschikbaar: false, reden: e.message });
    } finally { setBezig(false); }
  };
  if (!d) {
    return (
      <Box>
        <Button size="small" variant="outlined" onClick={laad} disabled={bezig} startIcon={bezig ? <CircularProgress size={14} /> : <MailOutlineIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none', color: ACCENT, borderColor: LIJN }}>Mailwisseling tonen</Button>
        <Typography sx={{ fontSize: 12, color: STIL, mt: 0.75 }}>Inkomende mail en onze antwoorden uit ikwilwerken@, rechtstreeks uit de mailbox. Er wordt niets opgeslagen.</Typography>
      </Box>
    );
  }
  if (!d.beschikbaar) return <Typography sx={{ color: STIL, fontSize: 13.5 }}>Correspondentie niet beschikbaar: {d.reden}</Typography>;
  return (
    <Box>
      {d.afgekapt && <Typography sx={{ fontSize: 12.5, color: STIL, mb: 1 }}>Afgekapt: alleen de laatste {d.max} berichten.</Typography>}
      {d.berichten.length === 0 ? <Typography sx={{ color: STIL, fontSize: 13.5 }}>Geen berichten gevonden.</Typography>
        : d.berichten.map((m) => <Bericht key={m.id} sid={sid} m={m} />)}
      <Button size="small" onClick={laad} disabled={bezig} sx={{ textTransform: 'none', color: ACCENT, px: 0 }}>Opnieuw ophalen</Button>
    </Box>
  );
}

function ScoreKnoppen({ waarde, actief, onKies }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.75 }}>
      {[1, 2, 3, 4].map((n) => (
        <Box key={n} role="button" tabIndex={-1} onClick={() => onKies(n)}
          sx={{ width: 34, height: 30, borderRadius: 1.5, display: 'grid', placeItems: 'center', fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
            border: `1px solid ${waarde === n ? SCORE_KLEUR[n] : actief ? '#cfd6e0' : LIJN}`,
            bgcolor: waarde === n ? SCORE_KLEUR[n] : '#fff', color: waarde === n ? '#fff' : STIL }}>{n}</Box>
      ))}
    </Box>
  );
}

function BeoordelingRij({ b, criteria }) {
  return (
    <Box sx={{ py: 1.25, borderTop: `1px solid ${LIJN}` }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: INKT, flex: 1 }}>{b.naam}</Typography>
        <Typography sx={{ fontSize: 12.5, color: OORDEEL_KLEUR[b.oordeel] || ACCENT, fontWeight: 600 }}>{OORDEEL_LABEL[b.oordeel]}</Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: STIL }}>
        {criteria.map((c) => `${c.naam} ${b.scores?.[c.id] ?? '–'}`).join(' · ')}
      </Typography>
      {b.opmerking && <Typography sx={{ fontSize: 13, color: INKT, mt: 0.5 }}>{b.opmerking}</Typography>}
    </Box>
  );
}

function Scorecard({ k, scores, setScores, oordeel, setOordeel, opmerking, setOpmerking, actief, setActief, onIndienen, bezig }) {
  const criteria = k.vacature?.criteria || [];
  const zichtbaar = useMemo(() => [...(k.mijn_beoordeling ? [k.mijn_beoordeling] : []), ...k.beoordelingen.filter((b) => b.fase === k.fase)], [k]);
  const gem = gemiddelden(zichtbaar, criteria);
  const tel = telOordelen(zichtbaar);
  const ingediendFase = k.telling?.[k.fase] || 0;
  const verborgen = ingediendFase - zichtbaar.length;

  if (!k.vacature) return <Typography sx={{ color: STIL, fontSize: 13.5 }}>Koppel deze sollicitatie eerst aan een vacature; dan komt de scorecard erbij.</Typography>;
  return (
    <Box>
      {k.mag_scoren ? (
        <Box>
          {criteria.map((c, i) => (
            <Box key={c.id} onClick={() => setActief(i)} sx={{ py: 1, px: 1, mx: -1, borderRadius: 2, bgcolor: actief === i ? ACCENT_ZACHT : 'transparent' }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: INKT }}>{c.naam}</Typography>
              {c.toelichting && <Typography sx={{ fontSize: 12, color: STIL, mb: 0.5 }}>{c.toelichting}</Typography>}
              <ScoreKnoppen waarde={scores[c.id]} actief={actief === i}
                onKies={(n) => { setScores((s) => ({ ...s, [c.id]: n })); setActief(Math.min(i + 1, criteria.length)); }} />
            </Box>
          ))}
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: STIL, mt: 1.5 }}>Jouw oordeel (sluit de procedure niet af)</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, p: actief === criteria.length ? 1 : 0, mx: actief === criteria.length ? -1 : 0, borderRadius: 2, bgcolor: actief === criteria.length ? ACCENT_ZACHT : 'transparent' }}>
            {['goedkeuren', 'afkeuren'].map((o) => (
              <Button key={o} fullWidth size="small" variant={oordeel === o ? 'contained' : 'outlined'} disableElevation onClick={() => setOordeel(o)}
                sx={{ textTransform: 'none', fontWeight: 600, borderColor: LIJN,
                  ...(oordeel === o ? { bgcolor: OORDEEL_KLEUR[o], '&:hover': { bgcolor: OORDEEL_KLEUR[o] } } : { color: STIL }) }}>
                {o === 'goedkeuren' ? 'Goedkeuren' : 'Afkeuren'}
              </Button>
            ))}
          </Box>
          <TextField fullWidth multiline minRows={2} size="small" placeholder="Waarom? (optioneel, max 500 tekens — valt onder het inzagerecht)"
            value={opmerking} onChange={(e) => setOpmerking(e.target.value.slice(0, 500))} sx={{ mt: 1.5 }} />
          <Button fullWidth variant="contained" disableElevation disabled={!scorecardCompleet(criteria, scores, oordeel) || bezig} onClick={onIndienen}
            sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700, bgcolor: ACCENT, '&:hover': { bgcolor: '#434f62' } }}>
            Indienen
          </Button>
          <Typography sx={{ fontSize: 11.5, color: STIL, mt: 1 }}>
            {ingediendFase ? `${ingediendFase} van ${k.aantal_beoordelaars} beoordelaars hebben gescoord; je ziet hun scores nadat je zelf hebt ingediend.`
              : 'Je bent de eerste. Scores van anderen zie je pas na je eigen beoordeling.'}
          </Typography>
        </Box>
      ) : (
        <Box>
          {!k.ben_beoordelaar && (
            <Typography sx={{ fontSize: 13, color: STIL, mb: 1 }}>
              {k.kan_bewerken
                ? 'Je bent geen beoordelaar van deze vacature — voeg jezelf toe bij Vacature bewerken.'
                : 'Je bent geen beoordelaar van deze vacature.'}
            </Typography>
          )}
          {zichtbaar.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 13, color: INKT, fontWeight: 600 }}>
                {tel.goedkeuren} goedgekeurd · {tel.afkeuren} afgekeurd
              </Typography>
              <Typography sx={{ fontSize: 12, color: STIL }}>
                Gemiddeld: {criteria.map((c) => `${c.naam} ${gem[c.id] ?? '–'}`).join(' · ')}
              </Typography>
            </Box>
          )}
          {zichtbaar.map((b) => <BeoordelingRij key={`${b.fase}-${b.beoordelaar}`} b={b} criteria={criteria} />)}
          {verborgen > 0 && (
            <Typography sx={{ fontSize: 12.5, color: STIL, mt: 1 }}>
              {verborgen} beoordeling{verborgen === 1 ? '' : 'en'} nog verborgen{k.kan_bewerken ? ' tot deze fase sluit (verzet de sollicitatie)' : ''}.
            </Typography>
          )}
          {!zichtbaar.length && !verborgen && <Typography sx={{ fontSize: 13, color: STIL }}>Nog geen beoordelingen in deze fase.</Typography>}
        </Box>
      )}
      {k.beoordelingen.some((b) => b.fase !== k.fase) && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={kop}>Eerdere fasen</Typography>
          {k.beoordelingen.filter((b) => b.fase !== k.fase).map((b) => <BeoordelingRij key={`${b.fase}-${b.beoordelaar}`} b={b} criteria={criteria} />)}
        </Box>
      )}
    </Box>
  );
}

export default function SollicitantKaart() {
  const { id } = useParams();
  const sid = Number(id);
  const navigate = useNavigate();
  const [k, setK] = useState(null);
  const [fout, setFout] = useState(null);
  const [tab, setTab] = useState('scorecard');
  const [scores, setScores] = useState({});
  const [oordeel, setOordeel] = useState(null);
  const [opmerking, setOpmerking] = useState('');
  const [actief, setActief] = useState(0);
  const [bezig, setBezig] = useState(false);
  const [notitie, setNotitie] = useState('');
  const [conceptMenu, setConceptMenu] = useState(null);
  const [procedureMenu, setProcedureMenu] = useState(null);
  const [voorbeeld, setVoorbeeld] = useState(null);
  const [vacatures, setVacatures] = useState([]);
  const [platform, setPlatform] = useState(null);   // uit de brief: "via LinkedIn" + kandidaatnaam
  const [afgeleid, setAfgeleid] = useState(undefined);   // uit de brief: waar een concept naartoe gaat (alleen edit)

  // Alleen antwoorden voor de sollicitant die nú in de url staat: een laat antwoord of een undo-callback
  // van een vorige kaart mag de kaart niet overschrijven (anders scoor je B met de kaart van A in beeld).
  const actueel = useRef(sid);
  actueel.current = sid;
  const laad = useCallback(() => {
    const voor = sid;
    return wervingApi.kaart(voor)
      .then((x) => { if (actueel.current === voor) { setK(x); setFout(null); } })
      .catch((e) => { if (actueel.current === voor) setFout(e); });
  }, [sid]);
  const kaartKlopt = !!k && Number(k.id) === sid;
  // Andere sollicitant: alles wat je aan het typen was hoort bij de vorige (ook een half geschreven notitie).
  useEffect(() => { setK(null); setScores({}); setOordeel(null); setOpmerking(''); setNotitie(''); setActief(0); setPlatform(null); setAfgeleid(undefined); laad(); }, [laad]);
  useEffect(() => registreerOngedaanSneltoets(), []);
  const kanBewerken = !!k?.kan_bewerken;
  useEffect(() => {
    // Ook als hij al gekoppeld is (door de AI of iemand anders): Joanne kan hem altijd zelf (opnieuw) koppelen.
    if (kanBewerken) wervingApi.overzicht().then((d) => setVacatures(d.vacatures || [])).catch(() => {});
  }, [kanBewerken]);

  const nav = useMemo(() => buren(k?.stapel, sid), [k, sid]);
  const criteria = useMemo(() => k?.vacature?.criteria || [], [k]);

  const indienen = useCallback(async () => {
    if (!k?.mag_scoren || Number(k.id) !== sid || !scorecardCompleet(criteria, scores, oordeel) || bezig) return;
    setBezig(true);
    try {
      await wervingApi.beoordeel(sid, { scores, oordeel, opmerking });
      toast.success('Beoordeling ingediend');
      if (actueel.current !== sid) return;
      const volgende = volgendeTeBeoordelen(k.stapel.map((s) => (s.id === sid ? { ...s, te_beoordelen: false } : s)), sid);
      if (volgende && volgende !== sid) navigate(`/werving/sollicitatie/${volgende}`); else laad();
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  }, [k, criteria, scores, oordeel, opmerking, sid, bezig, navigate, laad]);

  // Sneltoetsen (via een ref, zodat de listener altijd de laatste staat ziet)
  const staat = useRef({});
  staat.current = { k, nav, actief, criteria, indienen };
  useEffect(() => {
    const h = (e) => {
      const a = sneltoets(e);
      if (!a) return;
      const { k: kk, nav: n, actief: act, criteria: crit, indienen: dien } = staat.current;
      if (!kk) return;
      if (a.soort === 'volgende' && n.volgende) { e.preventDefault(); navigate(`/werving/sollicitatie/${n.volgende}`); }
      else if (a.soort === 'vorige' && n.vorige) { e.preventDefault(); navigate(`/werving/sollicitatie/${n.vorige}`); }
      else if (!kk.mag_scoren) return;
      else if (a.soort === 'score' && crit[act]) { e.preventDefault(); setScores((s) => ({ ...s, [crit[act].id]: a.waarde })); setActief(Math.min(act + 1, crit.length)); }
      else if (a.soort === 'oordeel') { e.preventDefault(); setOordeel(a.waarde); }
      else if (a.soort === 'indienen') { e.preventDefault(); dien(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [navigate]);

  const zetFase = async (naar) => {
    if (!kaartKlopt || naar === k.fase) return;
    const label = k.fasen.find((f) => f.sleutel === naar)?.label;
    if (EIND_FASEN.includes(naar) && !(await bevestig({
      titel: `${k.naam} naar ${label}?`,
      tekst: naar === 'afgewezen'
        ? 'De procedure stopt; de gegevens worden 4 weken bewaard (1 jaar met toestemming). Er gaat geen mail uit — zet zo nodig een afwijzing klaar als concept.'
        : 'De sollicitatie wordt als aangenomen gemarkeerd. Er gaat geen mail uit.',
      knop: `Naar ${label}`, gevaarlijk: naar === 'afgewezen',
    }))) return;
    const vorige = k.fase;
    try {
      await wervingApi.fase(sid, naar, vorige);
      biedOngedaanAan(`${k.naam} → ${label}`, async () => { await wervingApi.fase(sid, vorige, naar); await laad(); });
      laad();
    } catch (e) { toast.error(e.message); laad(); }
  };

  const zetGesprek = async (waarde) => {
    if (!kaartKlopt) return;
    const vorige = k.gesprek_op;
    try {
      await wervingApi.gesprek(sid, waarde);
      biedOngedaanAan(waarde ? 'Gespreksdatum opgeslagen' : 'Gespreksdatum gewist', async () => { await wervingApi.gesprek(sid, vorige ? `${vorige}Z` : null); await laad(); });
      laad();
    } catch (e) { toast.error(e.message); }
  };

  // AVG: de sollicitant vroeg (in een reply) om 1 jaar bewaren. Herberekent bewaren_tot op de server; ongedaan = vorige waarde.
  const zetToestemming = async () => {
    if (!kaartKlopt || k.geanonimiseerd) return;
    const vorige = k.toestemming || 'onbekend';
    const nieuw = vorige === 'ja' ? 'onbekend' : 'ja';
    try {
      await wervingApi.toestemming(sid, nieuw);
      biedOngedaanAan(nieuw === 'ja' ? 'Toestemming 1 jaar vastgelegd' : 'Toestemming 1 jaar ingetrokken',
        async () => { await wervingApi.toestemming(sid, vorige); await laad(); });
      laad();
    } catch (e) { toast.error(e.message); }
  };

  const concept = async (s) => {
    setConceptMenu(null);
    if (!kaartKlopt) return;
    // Zelfde regel als Antwoordadres: net gewist → het oude (opgeslagen) adres uit de brief niet meer tonen.
    const naar = k.antwoordadres || (afgeleid?.opgeslagen ? null : afgeleid?.adres);
    if (!(await bevestig({
      titel: `Concept "${s.naam}" klaarzetten?`,
      tekst: `Er komt een concept-antwoord in ikwilwerken@ → Concepten, gericht aan ${k.voornaam || 'de sollicitant'}${naar ? ` (${naar})` : ''}. Er wordt niets verstuurd: lees het na en verstuur het zelf in Outlook.`,
      knop: 'Concept klaarzetten',
    }))) return;
    try {
      const r = await wervingApi.concept(sid, s.id);
      if (r.status === 'voorbeeld') setVoorbeeld(r); else toast.success(r.melding, { duration: 8000 });
      laad();
    } catch (e) { toast.error(e.message); }
  };

  const plaatsNotitie = async () => {
    if (!notitie.trim() || !kaartKlopt) return;
    const voor = sid;
    try {
      const x = await wervingApi.notitie(voor, notitie.trim());
      if (actueel.current === voor) { setK(x); setNotitie(''); }
    } catch (e) { toast.error(e.message); }
  };

  if (fout) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', mt: 10, textAlign: 'center', px: 2 }}>
        <Typography sx={{ fontWeight: 700, color: INKT }}>{fout.status === 404 ? 'Sollicitatie niet gevonden' : 'Kon de sollicitatie niet laden'}</Typography>
        <Typography sx={{ color: STIL, mt: 1 }}>{fout.status === 404 ? 'Of je bent geen beoordelaar van deze vacature.' : fout.message}</Typography>
        <Button onClick={() => navigate('/werving')} sx={{ mt: 2, textTransform: 'none', color: ACCENT }}>Naar Werving</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 2, md: 3 }, pb: 6 }}>
      {/* Kruimelpad + 3 van 12 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, minHeight: 36 }}>
        <Typography sx={{ fontSize: 13, color: STIL, flex: 1, minWidth: 0 }}>
          <Box component="span" role="button" onClick={() => navigate('/werving')} sx={{ cursor: 'pointer', '&:hover': { color: INKT } }}>Werving</Box>
          {k?.vacature && <> › <Box component="span" role="button" onClick={() => navigate(`/werving/vacature/${k.vacature.id}`)} sx={{ cursor: 'pointer', '&:hover': { color: INKT } }}>{k.vacature.titel}</Box></>}
          {k && !k.vacature && ' › Te koppelen'}
          {k && <> › <Box component="span" sx={{ color: INKT }}>{k.naam}</Box></>}
        </Typography>
        {/* Op brede schermen staat de stapel links al; dan geen tweede navigatie */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.5 }}>
          {nav.totaal > 0 && <Typography sx={{ fontSize: 13, color: STIL }}>{nav.positie} van {nav.totaal}</Typography>}
          <Tooltip title="Vorige (k)"><span><IconButton size="small" disabled={!nav.vorige} onClick={() => navigate(`/werving/sollicitatie/${nav.vorige}`)}><KeyboardArrowUpIcon /></IconButton></span></Tooltip>
          <Tooltip title="Volgende (j)"><span><IconButton size="small" disabled={!nav.volgende} onClick={() => navigate(`/werving/sollicitatie/${nav.volgende}`)}><KeyboardArrowDownIcon /></IconButton></span></Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', border: `1px solid ${LIJN}`, borderRadius: 3, bgcolor: '#fff', overflow: 'hidden', minHeight: 'calc(100vh - 170px)' }}>
        {/* Stapel */}
        <Box sx={{ width: 264, flexShrink: 0, bgcolor: KOLOM_BG, borderRight: `1px solid ${LIJN}`, p: 1.25, display: { xs: 'none', md: 'block' }, overflowY: 'auto' }}>
          <Typography sx={{ ...kop, px: 1.25, pt: 0.5, pb: 1 }}>{k?.vacature ? 'Sollicitanten' : 'Te koppelen'}</Typography>
          {(k?.stapel || []).map((s) => (
            <Box key={s.id} role="button" onClick={() => navigate(`/werving/sollicitatie/${s.id}`)}
              sx={{ px: 1.25, py: 0.9, borderRadius: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
                bgcolor: s.id === sid ? '#fff' : 'transparent', boxShadow: s.id === sid ? '0 1px 2px rgba(15,23,42,.06)' : 'none', '&:hover': { bgcolor: '#fff' } }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar naam={s.naam} maat={28} />
                {s.te_beoordelen && <Box title="Nog door jou te beoordelen" sx={{ position: 'absolute', top: -1, right: -1, width: 8, height: 8, borderRadius: '50%', bgcolor: ACCENT, border: '2px solid #fafbfc' }} />}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: s.id === sid ? 700 : 500, color: INKT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.naam}</Typography>
                <FaseChip klein fase={s.fase} label={k.fasen.find((f) => f.sleutel === s.fase)?.label} />
              </Box>
            </Box>
          ))}
        </Box>

        {!k ? <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress size={24} /></Box> : (
          <>
            {/* Midden */}
            <Box sx={{ flex: 1, minWidth: 0, px: { xs: 2, md: 4 }, py: { xs: 2.5, md: 3.5 }, overflowY: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar naam={k.naam} maat={44} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 26, fontWeight: 800, color: INKT, fontFamily: '"Manrope", system-ui, sans-serif', lineHeight: 1.2 }}>
                    {k.naam}{platform?.kandidaat && platform.kandidaat !== k.naam ? <Box component="span" sx={{ fontSize: 15, fontWeight: 500, color: STIL }}> · {platform.kandidaat}</Box> : null}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                    <FaseChip fase={k.fase} label={k.fasen.find((f) => f.sleutel === k.fase)?.label} />
                    <Typography sx={{ fontSize: 13.5, color: STIL }}>
                      {[`Binnen ${dagenGeleden(k.ontvangen_op)}`, `via ${platform?.platform_label || k.bron}`,
                        k.gesprek_op && `gesprek ${gesprekLabel(k.gesprek_op)}`, k.demo && 'demo'].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {k.kan_bewerken && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {k.vacature && (
                    <TextField select size="small" label="Fase" value={k.fase} onChange={(e) => zetFase(e.target.value)} sx={{ minWidth: 190 }}>
                      {k.fasen.map((f) => <MenuItem key={f.sleutel} value={f.sleutel}>{f.label}</MenuItem>)}
                    </TextField>
                  )}
                  <KoppelVelden k={k} sid={sid} vacatures={vacatures} stagetypes={k.stagetypes} onKlaar={laad} />
                  {k.vacature && (
                    <>
                      <Tooltip title="Procedure"><IconButton size="small" aria-label="Procedure" onClick={(e) => setProcedureMenu(e.currentTarget)}
                        sx={{ border: `1px solid ${LIJN}`, borderRadius: 2 }}><MoreHorizIcon fontSize="small" /></IconButton></Tooltip>
                      <Menu anchorEl={procedureMenu} open={Boolean(procedureMenu)} onClose={() => setProcedureMenu(null)}>
                        {k.fase !== 'afgewezen' && (
                          <MenuItem onClick={() => { setProcedureMenu(null); zetFase('afgewezen'); }}>
                            <Box><Typography sx={{ fontSize: 14 }}>Procedure beëindigen (afwijzen)</Typography>
                              <Typography sx={{ fontSize: 12, color: STIL }}>Zet de sollicitatie in Afgewezen; er gaat geen mail uit</Typography></Box>
                          </MenuItem>
                        )}
                        {k.fase !== 'aangenomen' && (
                          <MenuItem onClick={() => { setProcedureMenu(null); zetFase('aangenomen'); }}>
                            <Typography sx={{ fontSize: 14 }}>Aannemen</Typography>
                          </MenuItem>
                        )}
                      </Menu>
                    </>
                  )}
                  {k.vacature && k.fase === 'gesprek' && (
                    <TextField size="small" type="datetime-local" label="Gesprek" InputLabelProps={{ shrink: true }}
                      key={`g-${sid}-${k.gesprek_op || ''}`} defaultValue={naarLokaalInvoer(k.gesprek_op)}
                      onBlur={(e) => { const w = vanLokaalInvoer(e.target.value); const oud = k.gesprek_op ? vanLokaalInvoer(naarLokaalInvoer(k.gesprek_op)) : null; if (w !== oud) zetGesprek(w); }}
                      sx={{ minWidth: 220 }} />
                  )}
                  <Button size="small" variant="outlined" onClick={(e) => setConceptMenu(e.currentTarget)} disabled={k.geanonimiseerd}
                    sx={{ textTransform: 'none', color: ACCENT, borderColor: LIJN }}>Concept klaarzetten</Button>
                  <Menu anchorEl={conceptMenu} open={Boolean(conceptMenu)} onClose={() => setConceptMenu(null)}
                    PaperProps={{ sx: { maxWidth: 420 } }}>
                    {(k.concept_sjablonen || []).map((s) => (
                      <MenuItem key={s.id} onClick={() => concept(s)}>
                        <Box>
                          <Typography sx={{ fontSize: 14 }}>{s.naam}</Typography>
                          {s.past_bij_fase && <Typography sx={{ fontSize: 12, color: STIL }}>past bij de fase {k.fasen.find((f) => f.sleutel === k.fase)?.label}</Typography>}
                        </Box>
                      </MenuItem>
                    ))}
                    {k.concept_advies && (
                      <Box sx={{ px: 2, py: 1.25, borderTop: (k.concept_sjablonen || []).length ? `1px solid ${LIJN}` : 'none' }}>
                        <Typography sx={{ fontSize: 13, color: INKT, whiteSpace: 'normal' }}>{conceptAdviesTekst(k.concept_advies)}</Typography>
                        <Button size="small" onClick={() => { setConceptMenu(null); navigate(beheerPad(k.concept_advies.beheer_url)); }}
                          sx={{ textTransform: 'none', color: ACCENT, px: 0, mt: 0.5 }}>
                          Sjablonen aanzetten in Admin → Algemene mailboxen → ikwilwerken@
                        </Button>
                      </Box>
                    )}
                  </Menu>
                  {!k.geanonimiseerd && (
                    <Tooltip title="Vraagt de sollicitant (bv. in een reply) om de gegevens een jaar te bewaren? Dan bewaren we tot 1 jaar na de procedure in plaats van 4 weken.">
                      <Button size="small" variant={k.toestemming === 'ja' ? 'contained' : 'outlined'} disableElevation onClick={zetToestemming}
                        aria-pressed={k.toestemming === 'ja'}
                        sx={{ textTransform: 'none', ...(k.toestemming === 'ja' ? { bgcolor: ACCENT_ZACHT, color: ACCENT, '&:hover': { bgcolor: ACCENT_ZACHT } } : { color: ACCENT, borderColor: LIJN }) }}>
                        {k.toestemming === 'ja' ? '✓ Toestemming 1 jaar' : 'Toestemming 1 jaar'}
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              )}
              {k.kan_bewerken && !k.geanonimiseerd && <Antwoordadres k={k} sid={sid} afgeleid={afgeleid} onKlaar={laad} />}
              {k.kan_bewerken && bewaarLabel(k) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.25, flexWrap: 'wrap' }}>
                  <Box component="span" sx={{ fontSize: 12, color: STIL, bgcolor: KOLOM_BG, border: `1px solid ${LIJN}`, borderRadius: 10, px: 1, py: 0.2 }}>
                    {bewaarLabel(k)}
                  </Box>
                  {k.toestemming === 'talentpool' && (
                    <Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: faseKleur('aanbod').tekst, bgcolor: faseKleur('aanbod').bg, borderRadius: 10, px: 0.9, py: 0.2 }}>talentpool</Box>
                  )}
                </Box>
              )}

              {k.geanonimiseerd ? (
                <Typography sx={{ mt: 4, color: STIL }}>Deze sollicitatie is geanonimiseerd; alleen fase en scores zijn bewaard.</Typography>
              ) : (
                <>
                  {k.ai_samenvatting && (
                    <Accordion disableGutters elevation={0} sx={{ mt: 3, border: `1px solid ${LIJN}`, borderRadius: '12px !important', '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography sx={{ fontSize: 13.5, fontWeight: 600, color: INKT }}>Samenvatting <Box component="span" sx={{ color: STIL, fontWeight: 400 }}>· AI, feitelijk — geen oordeel</Box></Typography></AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}><Typography sx={{ fontSize: 14, color: INKT, lineHeight: 1.6 }}>{k.ai_samenvatting}</Typography></AccordionDetails>
                    </Accordion>
                  )}

                  {k.portfolio_urls.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography sx={kopSectie}>Portfolio</Typography>
                      <Box><Portfolio urls={k.portfolio_urls} /></Box>
                    </Box>
                  )}

                  <Box sx={{ mt: 3 }}>
                    <Typography sx={kopSectie}>Brief</Typography>
                    <Brief sid={sid} onPlatform={setPlatform} onAdres={setAfgeleid} />
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Typography sx={kopSectie}>Bijlagen</Typography>
                    {k.bijlagen.length === 0 ? <Typography sx={{ color: STIL, fontSize: 13.5 }}>Geen bijlagen.</Typography>
                      : k.bijlagen.map((b) => <Bijlage key={`${sid}-${b.index}`} sid={sid} b={b} />)}
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Typography sx={kopSectie}>Correspondentie</Typography>
                    <Correspondentie sid={sid} />
                  </Box>
                </>
              )}
            </Box>

            {/* Rechts: scorecard / tijdlijn / notities */}
            <Box sx={{ width: { xs: '100%', lg: 360 }, flexShrink: 0, borderLeft: `1px solid ${LIJN}`, display: { xs: 'none', lg: 'block' }, overflowY: 'auto' }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
                sx={{ borderBottom: `1px solid ${LIJN}`, minHeight: 44, '& .MuiTab-root': { textTransform: 'none', minHeight: 44, fontSize: 13.5, color: STIL },
                  '& .MuiTab-root.Mui-selected': { color: ACCENT, fontWeight: 700 }, '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 2.5 } }}>
                <Tab value="scorecard" label={k.mag_scoren ? 'Scorecard •' : 'Scorecard'} />
                <Tab value="tijdlijn" label="Tijdlijn" />
                <Tab value="notities" label={`Notities${k.notities.length ? ` (${k.notities.length})` : ''}`} />
              </Tabs>
              <Box sx={{ p: 2.25 }}>
                {tab === 'scorecard' && (
                  <>
                    <Scorecard k={k} scores={scores} setScores={setScores} oordeel={oordeel} setOordeel={setOordeel}
                      opmerking={opmerking} setOpmerking={setOpmerking} actief={actief} setActief={setActief} onIndienen={indienen} bezig={bezig} />
                    <Typography sx={{ fontSize: 12.5, color: STIL, mt: 3, lineHeight: 1.6 }}>Sneltoetsen: j / k vorige/volgende · 1–4 score · g goedkeuren · x afkeuren · Enter indienen</Typography>
                  </>
                )}
                {tab === 'tijdlijn' && (k.tijdlijn.length === 0 ? <Typography sx={{ color: STIL, fontSize: 13 }}>Nog niets gebeurd.</Typography>
                  : [...k.tijdlijn].reverse().map((t, i) => (
                    <Box key={i} sx={{ py: 1, borderTop: i ? `1px solid ${LIJN}` : 'none' }}>
                      <Typography sx={{ fontSize: 13.5, color: INKT }}>{t.label}</Typography>
                      <Typography sx={{ fontSize: 12, color: STIL }}>{[datumTijd(t.op), t.door].filter(Boolean).join(' · ')}</Typography>
                    </Box>
                  )))}
                {tab === 'notities' && (
                  <Box>
                    <TextField fullWidth multiline minRows={2} size="small" placeholder="Notitie (zakelijk: valt onder het inzagerecht van de sollicitant)"
                      value={notitie} onChange={(e) => setNotitie(e.target.value.slice(0, 2000))} />
                    <Button size="small" onClick={plaatsNotitie} disabled={!notitie.trim()} sx={{ mt: 1, textTransform: 'none', color: ACCENT }}>Toevoegen</Button>
                    {k.notities_verborgen > 0 && (
                      <Typography sx={{ fontSize: 12.5, color: STIL, mt: 1 }}>
                        {k.notities_verborgen} notitie{k.notities_verborgen === 1 ? '' : 's'} van anderen verborgen tot je zelf hebt gescoord{k.kan_bewerken ? ' (of de fase sluit)' : ''}.
                      </Typography>
                    )}
                    {[...k.notities].reverse().map((n) => (
                      <Box key={n.id} sx={{ py: 1.25, borderTop: `1px solid ${LIJN}` }}>
                        <Typography sx={{ fontSize: 12, color: STIL }}>{n.auteur} · {datumTijd(n.op)}</Typography>
                        <Typography sx={{ fontSize: 13.5, color: INKT, whiteSpace: 'pre-wrap' }}>{n.tekst}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* Smal scherm: scorecard onder de inhoud */}
      {k && (
        <Box sx={{ display: { xs: 'block', lg: 'none' }, mt: 2, border: `1px solid ${LIJN}`, borderRadius: 3, bgcolor: '#fff', p: 2 }}>
          <Typography sx={{ ...kop, mb: 1 }}>Scorecard</Typography>
          <Scorecard k={k} scores={scores} setScores={setScores} oordeel={oordeel} setOordeel={setOordeel}
            opmerking={opmerking} setOpmerking={setOpmerking} actief={actief} setActief={setActief} onIndienen={indienen} bezig={bezig} />
        </Box>
      )}

      <Dialog open={Boolean(voorbeeld)} onClose={() => setVoorbeeld(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Voorbeeld concept</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: STIL, mb: 1.5 }}>{voorbeeld?.melding}</Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 14, color: INKT, bgcolor: KOLOM_BG, p: 2, borderRadius: 2 }}>{voorbeeld?.tekst}</Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setVoorbeeld(null)} sx={{ textTransform: 'none', color: ACCENT }}>Sluiten</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
